import React, { useReducer, useEffect, useMemo } from 'react';
import GenericTable from '../../NewUI/Components/GenericTable/GenericTable';
import {
  initialTableState,
  genericTableReducer
} from '../../NewUI/Components/GenericTable/genericTableReducer';
import {
  TSortableColumn,
  GenericTableData,
  TPaginationState
} from '../../NewUI/Components/GenericTable/types';
import { Typography, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { classes } from '../../NewUI/EditJob/Questions/styles';

interface ChartDataTableProps {
  chartData: {
    labels: string[];
    datasets: { label: string; data: number[] }[];
  };
  label?: string;
  chartTitle?: string;
}

const ChartDataTable: React.FC<ChartDataTableProps> = ({
  chartData,
  label = 'Chart Data',
  chartTitle
}) => {
  // Memoize columns to prevent unnecessary re-renders
  const columns: TSortableColumn[] = useMemo(
    () => [
      {
        id: 'label',
        label: 'Label',
        sortable: false,
        canToggle: false,
        enabled: true
      },
      ...chartData.datasets.map((ds, i) => ({
        id: `dataset_${i}`,
        label: ds.label,
        sortable: false,
        canToggle: false,
        enabled: true
      }))
    ],
    [chartData.datasets]
  );

  // Memoize items to prevent unnecessary re-renders
  const items: GenericTableData[] = useMemo(
    () =>
      chartData.labels.slice(0, 5).map((labelValue, rowIdx) => {
        const row: GenericTableData = { id: rowIdx, label: labelValue };
        chartData.datasets.forEach((ds, i) => {
          row[`dataset_${i}`] = ds.data[rowIdx];
        });
        return row;
      }),
    [chartData.labels, chartData.datasets]
  );

  // Table state
  const [state, dispatch] = useReducer(
    genericTableReducer,
    initialTableState(columns, { sortBy: 'label', sortOrder: 'asc' })
  );

  // Update columns when chartData changes
  useEffect(() => {
    dispatch({ type: 'SET_COLUMNS', payload: columns });
  }, [columns]);

  const paginationState: TPaginationState<string> = state.searchSettings;

  // Simple row renderer
  const TableRows: React.FC<{ column: TSortableColumn; item: GenericTableData }> = ({
    column,
    item
  }) => (
    <td style={column.id === 'label' ? { whiteSpace: 'nowrap', minWidth: '100px' } : {}}>
      {item[column.id]}
    </td>
  );

  // Simple header style
  const headerStyle = (id: string) => ({
    ...(id === 'label' && { minWidth: '100px' })
  });

  return (
    <Box>
      <Typography color="#084D6D" variant="h5" fontWeight={700} sx={{ fontFamily: 'inherit' }}>
        {chartTitle}
      </Typography>
      <Box sx={classes.noticeContainer}>
        <InfoIcon sx={classes.infoIcon} />
        <Box
          sx={{
            marginLeft: 1,
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: 1.5
          }}
        >
          This is a preview of the first five rows of the data. For the full dataset, please export
          the CSV via the button below.
        </Box>
      </Box>
      <GenericTable
        items={items}
        isLoadingItems={false}
        dispatch={dispatch}
        tableState={state.tableState}
        paginationState={paginationState}
        headerStyle={headerStyle}
        TableRows={TableRows}
        label={label}
        allowMultipleSelection={false}
        customEmptyStateMessage="No data to display."
        emptyMessageOverride={null}
      />
    </Box>
  );
};

export default ChartDataTable;
